import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useBaseCommentsStore } from '@getaccept/editor-lib/src/store/base-comments.store';
import { CommentStatus } from '../types/comment-status';
import type { UpdateCommentDto } from '@getaccept/lib-shared/src/contextual-commenting/types/update-comment-dto';
import type { CommentReadEvent } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-read-event';
import type { Comment, Reply } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';

export const useContextualCommentingStore = defineStore('contextual-commenting', () => {
  const baseCommentsStore = useBaseCommentsStore();
  const {
    comments: baseComments,
    hoveredCommentId,
    updatedTextSelections,
    editorHasRendered,
    previouslySelectedCommentId,
  } = storeToRefs(baseCommentsStore);

  const comments = computed(() => baseComments.value as Comment[]);

  const commentHistoryComments: ComputedRef<Comment[]> = computed(() =>
    comments.value.filter(
      (comment: Comment) =>
        comment.status !== CommentStatus.Draft &&
        CommentsHelper.isViewableComment(comment, currentUserId.value, [
          CommentStatus.Active,
          CommentStatus.Resolved,
        ])
    )
  );

  const viewableComments: ComputedRef<Comment[]> = computed(() =>
    commentsHidden.value
      ? []
      : comments.value.filter((comment: Comment) =>
          CommentsHelper.isViewableComment(comment, currentUserId.value, [CommentStatus.Active])
        )
  );

  const setComments = (newComments: Comment[]) => {
    baseCommentsStore.setComments(newComments);
  };

  const currentUserId = ref('');
  const setCurrentUserId = (userId: string) => {
    currentUserId.value = userId;
  };

  const updateComment = (payload: Partial<UpdateCommentDto>) => {
    const updatedComments = comments.value.map((existingComment: Comment) =>
      existingComment.id === payload.commentId
        ? { ...existingComment, ...payload }
        : existingComment
    );

    baseCommentsStore.setComments(updatedComments);
  };

  const replaceComment = (comment: Comment, idToReplace = '') => {
    const updatedComments = comments.value.map((existingComment: Comment) =>
      [comment.id, idToReplace].includes(existingComment.id) ? comment : existingComment
    );

    baseCommentsStore.setComments(updatedComments);
  };

  const addComment = (comment: Comment) => {
    baseCommentsStore.setComments([...comments.value, comment]);
  };

  const removeComment = (commentId: string) => {
    baseCommentsStore.setComments(
      comments.value.filter((comment: Comment) => comment.id !== commentId)
    );
  };

  const selectedCommentId = ref('');

  const selectComment = (commentId: string) => {
    if (commentId) {
      baseCommentsStore.setPreviouslySelectedCommentId(commentId);
    }
    selectedCommentId.value = commentId;
  };

  const selectedComment = computed(() =>
    comments.value.find(comment => comment.id === selectedCommentId.value)
  );

  const hoverComment = (commentId: string) => {
    baseCommentsStore.setHoveredCommentId(commentId);
  };

  const hoveredComment = computed(() =>
    comments.value.find(comment => comment.id === hoveredCommentId.value)
  );

  const addReply = (commentId: string, reply: Reply) => {
    baseCommentsStore.setComments(CommentsHelper.addReply(reply, commentId, comments.value));
  };

  const removeReply = (commentId: string, replyId: string) => {
    const updatedComments = comments.value.map((comment: Comment) =>
      comment.id !== commentId
        ? comment
        : {
            ...comment,
            replies: comment.replies.filter((reply: Reply) => reply.id !== replyId),
          }
    );

    baseCommentsStore.setComments(updatedComments);
  };

  const replaceReply = (commentId: string, newReply: Reply, idToReplace: string) => {
    const updatedComments = comments.value.map((comment: Comment) =>
      comment.id !== commentId
        ? comment
        : {
            ...comment,
            replies: comment.replies.map((existingReply: Reply) =>
              existingReply.id === idToReplace ? newReply : existingReply
            ),
          }
    );

    baseCommentsStore.setComments(updatedComments);
  };

  const addReadEvent = (commentId: string, readEvent: CommentReadEvent) => {
    baseCommentsStore.setComments(CommentsHelper.addRead(readEvent, commentId, comments.value));
  };

  const addReplyReadEvent = (commentId: string, replyId: string, readEvent: CommentReadEvent) => {
    baseCommentsStore.setComments(
      CommentsHelper.addReplyRead(readEvent, replyId, commentId, comments.value)
    );
  };

  const reset = () => {
    baseCommentsStore.reset();
    selectedCommentId.value = '';
    currentUserId.value = '';
  };

  const commentsHidden = ref(false);
  const toggleHideComments = () => {
    commentsHidden.value = !commentsHidden.value;
  };

  const isCommentHistoryOpen = ref(false);
  const toggleCommentHistory = (value: boolean) => {
    isCommentHistoryOpen.value = value;
  };

  const draftIdUpdating = ref('');
  const draftIdUpdated = ref('');

  const setDraftIdUpdating = (commentId: string) => {
    draftIdUpdating.value = commentId;
  };

  const setDraftIdUpdated = (commentId: string) => {
    draftIdUpdated.value = commentId;
    draftIdUpdating.value = '';
  };

  return {
    setComments,

    setCurrentUserId,

    updateComment,
    addComment,
    removeComment,
    replaceComment,

    selectComment,
    selectedCommentId,
    selectedComment,
    previouslySelectedCommentId,

    hoverComment,
    hoveredCommentId,
    hoveredComment,

    addReply,
    replaceReply,
    removeReply,

    addReadEvent,
    addReplyReadEvent,

    reset,

    comments,
    viewableComments,

    updatedTextSelections,
    commentHistoryComments,

    commentsHidden,
    toggleHideComments,

    isCommentHistoryOpen,
    toggleCommentHistory,

    setDraftIdUpdating,
    setDraftIdUpdated,
    draftIdUpdating,
    draftIdUpdated,

    editorHasRendered,
  };
});
